import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneCall, PhoneIncoming, PhoneOutgoing, Mic, MicOff, PhoneMissed, Clock, TrendingUp, Users, DollarSign, Activity, Play, Pause, Volume2, VolumeX, UserPlus, AlertCircle, Download, Forward, Info, Filter, Calendar, BarChart3, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import LiveCallConsole from '@/components/LiveCallConsole';

const VoiceAI = () => {
  const navigate = useNavigate();
  const [activeCalls, setActiveCalls] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: '7d',
    botType: 'all',
    outcome: 'all',
    status: 'all'
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchActiveCalls, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    await Promise.all([fetchActiveCalls(), fetchRecentCalls(), fetchAnalytics()]);
    setLoading(false);
  };

  const fetchActiveCalls = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/voice-ai/calls?status=in_progress`);
      if (res.ok) {
        const data = await res.json();
        setActiveCalls(data);
      }
    } catch (err) {
      console.error('Failed to fetch active calls:', err);
    }
  };

  const fetchRecentCalls = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/voice-ai/calls?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setRecentCalls(data);
      }
    } catch (err) {
      console.error('Failed to fetch recent calls:', err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/voice-ai/analytics`);
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'completed': 'default',
      'in_progress': 'default',
      'no_answer': 'secondary',
      'failed': 'destructive',
      'voicemail': 'secondary'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status?.replace('_', ' ')}</Badge>;
  };

  const getOutcomeBadge = (outcome) => {
    const colors = {
      'qualified': 'bg-green-500',
      'appointment_scheduled': 'bg-blue-500',
      'escalated': 'bg-orange-500',
      'not_interested': 'bg-red-500',
      'callback_requested': 'bg-yellow-500'
    };
    return outcome ? (
      <Badge className={`${colors[outcome]} text-white`}>{outcome.replace('_', ' ')}</Badge>
    ) : null;
  };

  const getSentimentColor = (score) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 4) return 'text-green-500';
    if (score >= 3) return 'text-blue-500';
    if (score >= 2) return 'text-yellow-500';
    return 'text-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8">
        <div className="animate-pulse">Loading Voice AI System...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Phone className="h-8 w-8 text-primary" />
                Legion Voice AI Agents
              </h1>
              <p className="text-muted-foreground mt-1">
                24/7 Autonomous Voice Acquisition System
              </p>
            </div>
            <Button size="lg" onClick={() => navigate('/voice-ai/config')}>
              <PhoneCall className="h-4 w-4 mr-2" />
              Configure Agents
            </Button>
          </div>
          {/* Info Banner */}
          <div className="mt-4 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="text-sm text-blue-900 dark:text-blue-100">
                <p className="font-semibold mb-1">Voice Agents vs Legion Bots</p>
                <p className="text-blue-800 dark:text-blue-200">
                  <strong>Voice Agents</strong> handle phone calls with natural conversation, pacing, and tone. 
                  <strong className="ml-1">Legion Bots</strong> handle business logic, underwriting, and multi-channel messaging (SMS/Email).
                  Together they create a complete autonomous acquisition system.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Total Calls
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics?.total_calls || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics?.inbound_calls || 0} in / {analytics?.outbound_calls || 0} out
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Answer Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics?.answer_rate?.toFixed(1) || 0}%</div>
              <p className="text-xs text-muted-foreground mt-1">
                Industry avg: 45%
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Qualified
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics?.qualified_leads || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics?.qualification_rate?.toFixed(1) || 0}% qual rate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Appointments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics?.appointments_scheduled || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics?.appointment_conversion_rate?.toFixed(1) || 0}% conversion
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Avg Duration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {analytics?.average_call_duration ? `${Math.floor(analytics.average_call_duration / 60)}m` : '0m'}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics?.average_call_duration ? `${Math.floor(analytics.average_call_duration % 60)}s` : '0s'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Sentiment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${getSentimentColor(analytics?.average_sentiment)}`}>
                {analytics?.average_sentiment?.toFixed(1) || '0.0'}/5
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Deal score: {analytics?.average_deal_score?.toFixed(0) || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => setFilters({ dateRange: '7d', botType: 'all', outcome: 'all', status: 'all' })}>
                Reset Filters
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Date Range</label>
                <Select value={filters.dateRange} onValueChange={(val) => setFilters(prev => ({ ...prev, dateRange: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24h">Last 24 Hours</SelectItem>
                    <SelectItem value="7d">Last 7 Days</SelectItem>
                    <SelectItem value="30d">Last 30 Days</SelectItem>
                    <SelectItem value="90d">Last 90 Days</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Bot Type</label>
                <Select value={filters.botType} onValueChange={(val) => setFilters(prev => ({ ...prev, botType: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Bots</SelectItem>
                    <SelectItem value="land">Land Scout</SelectItem>
                    <SelectItem value="wholesale">Wholesale</SelectItem>
                    <SelectItem value="section8">Section 8</SelectItem>
                    <SelectItem value="multifamily">Multifamily</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Outcome</label>
                <Select value={filters.outcome} onValueChange={(val) => setFilters(prev => ({ ...prev, outcome: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Outcomes</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="appointment_scheduled">Appointment Set</SelectItem>
                    <SelectItem value="escalated">Escalated</SelectItem>
                    <SelectItem value="not_interested">Not Interested</SelectItem>
                    <SelectItem value="callback_requested">Callback Requested</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Status</label>
                <Select value={filters.status} onValueChange={(val) => setFilters(prev => ({ ...prev, status: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="no_answer">No Answer</SelectItem>
                    <SelectItem value="voicemail">Voicemail</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <Tabs defaultValue="active" className="space-y-6">
          <TabsList>
            <TabsTrigger value="active">Active Calls ({activeCalls.length})</TabsTrigger>
            <TabsTrigger value="recent">Recent Calls</TabsTrigger>
            <TabsTrigger value="voicemail">Voicemail</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* Active Calls */}
          <TabsContent value="active">
            <Card>
              <CardHeader>
                <CardTitle>Active Calls</CardTitle>
                <CardDescription>Calls currently in progress</CardDescription>
              </CardHeader>
              <CardContent>
                {activeCalls.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Phone className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>No active calls</p>
                    <p className="text-sm mt-2">Active calls will appear here when Voice Agents are handling conversations</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeCalls.map(call => (
                      <ActiveCallCard key={call.id} call={call} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recent Calls */}
          <TabsContent value="recent">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Recent Calls</CardTitle>
                    <CardDescription>Call history with transcripts and recordings</CardDescription>
                  </div>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {recentCalls.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <PhoneCall className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>No call history yet</p>
                    <p className="text-sm mt-2">Your call records will appear here</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b">
                        <tr className="text-left text-sm text-muted-foreground">
                          <th className="pb-3 font-medium">Contact</th>
                          <th className="pb-3 font-medium">Bot</th>
                          <th className="pb-3 font-medium">Status</th>
                          <th className="pb-3 font-medium">Outcome</th>
                          <th className="pb-3 font-medium">Duration</th>
                          <th className="pb-3 font-medium">Sentiment</th>
                          <th className="pb-3 font-medium">Deal Score</th>
                          <th className="pb-3 font-medium">Date</th>
                          <th className="pb-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentCalls.map(call => (
                          <tr key={call.id} className="border-b hover:bg-secondary/50 cursor-pointer" onClick={() => setSelectedCall(call)}>
                            <td className="py-4">
                              <div className="flex items-center gap-2">
                                {call.direction === 'inbound' ? <PhoneIncoming className="h-4 w-4 text-green-500" /> : <PhoneOutgoing className="h-4 w-4 text-blue-500" />}
                                <div>
                                  <div className="font-medium">{call.caller_name || 'Unknown'}</div>
                                  <div className="text-xs text-muted-foreground">{call.phone_number}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <Badge variant="outline">Land Scout</Badge>
                            </td>
                            <td>{getStatusBadge(call.status)}</td>
                            <td>{getOutcomeBadge(call.outcome)}</td>
                            <td className="text-sm">
                              {call.duration_seconds ? `${Math.floor(call.duration_seconds / 60)}:${(call.duration_seconds % 60).toString().padStart(2, '0')}` : '-'}
                            </td>
                            <td>
                              <span className={getSentimentColor(call.sentiment_score)}>
                                {call.sentiment_score ? `${call.sentiment_score.toFixed(1)}/5` : '-'}
                              </span>
                            </td>
                            <td>
                              <Badge variant={call.deal_score >= 80 ? 'default' : 'secondary'}>
                                {call.deal_score || '-'}
                              </Badge>
                            </td>
                            <td className="text-sm text-muted-foreground">
                              {new Date(call.created_at).toLocaleDateString()}
                            </td>
                            <td>
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost">
                                  <Play className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="ghost">
                                  <Download className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Voicemail */}
          <TabsContent value="voicemail">
            <Card>
              <CardHeader>
                <CardTitle>Voicemail Inbox</CardTitle>
                <CardDescription>Auto-transcribed voicemail messages</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-muted-foreground">
                  <Volume2 className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No voicemail messages</p>
                  <p className="text-sm mt-2">Voicemails will be auto-transcribed and appear here</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduled */}
          <TabsContent value="scheduled">
            <Card>
              <CardHeader>
                <CardTitle>Scheduled Calls</CardTitle>
                <CardDescription>Upcoming automated call attempts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No scheduled calls</p>
                  <p className="text-sm mt-2">Your automated follow-up calls will appear here</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics */}
          <TabsContent value="analytics">
            <div className="grid gap-6">
              {/* Call Funnel */}
              <Card>
                <CardHeader>
                  <CardTitle>Call Funnel</CardTitle>
                  <CardDescription>Conversion through qualification stages</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <FunnelStage label="Connected" count={analytics?.total_calls || 0} percentage={100} />
                    <FunnelStage label="Qualified" count={analytics?.qualified_leads || 0} percentage={analytics?.qualification_rate || 0} />
                    <FunnelStage label="Offer Given" count={0} percentage={0} />
                    <FunnelStage label="Negotiated" count={0} percentage={0} />
                    <FunnelStage label="Appointment Set" count={analytics?.appointments_scheduled || 0} percentage={analytics?.appointment_conversion_rate || 0} />
                  </div>
                </CardContent>
              </Card>

              {/* Bot Performance */}
              <Card>
                <CardHeader>
                  <CardTitle>Voice Agent Performance</CardTitle>
                  <CardDescription>Success metrics by agent type</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <BotPerformance name="Land Scout" calls={0} qualified={0} rate={0} color="bg-green-500" />
                    <BotPerformance name="Wholesale" calls={0} qualified={0} rate={0} color="bg-blue-500" />
                    <BotPerformance name="Section 8" calls={0} qualified={0} rate={0} color="bg-orange-500" />
                    <BotPerformance name="Multifamily" calls={0} qualified={0} rate={0} color="bg-purple-500" />
                  </div>
                </CardContent>
              </Card>

              {/* Geography Insights */}
              <Card>
                <CardHeader>
                  <CardTitle>Geography Insights</CardTitle>
                  <CardDescription>Performance by location</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>No geographic data yet</p>
                    <p className="text-sm mt-2">Call data by state, county, and ZIP will appear here</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Call Details Modal */}
      {selectedCall && (
        <CallDetailsModal 
          call={selectedCall} 
          onClose={() => setSelectedCall(null)} 
        />
      )}

      {/* Live Call Console - Floating */}
      <LiveCallConsole />
    </div>
  );
};

// Active Call Card Component
const ActiveCallCard = ({ call }) => {
  const [muted, setMuted] = useState(false);

  return (
    <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center">
              <Phone className="h-6 w-6 text-white" />
            </div>
            <div className="absolute -top-1 -right-1">
              <span className="flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
            </div>
          </div>
          <div>
            <div className="font-semibold">{call.caller_name || call.phone_number}</div>
            <div className="text-sm text-muted-foreground">Voice Agent Active</div>
            <Badge className="mt-1 bg-green-500 text-white">Land Scout Bot</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setMuted(!muted)}>
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="outline">
            <UserPlus className="h-4 w-4 mr-2" />
            Transfer to Human
          </Button>
          <Button size="sm" variant="destructive">
            End Call
          </Button>
        </div>
      </div>
    </div>
  );
};

// Funnel Stage Component
const FunnelStage = ({ label, count, percentage }) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm text-muted-foreground">{count} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="w-full bg-secondary rounded-full h-3">
        <div 
          className="bg-primary rounded-full h-3 transition-all"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

// Bot Performance Component
const BotPerformance = ({ name, calls, qualified, rate, color }) => {
  return (
    <div className="flex items-center justify-between p-3 border rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`h-8 w-8 rounded-full ${color} flex items-center justify-center`}>
          <Phone className="h-4 w-4 text-white" />
        </div>
        <div>
          <div className="font-medium">{name}</div>
          <div className="text-xs text-muted-foreground">{calls} calls • {qualified} qualified</div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-lg font-bold">{rate.toFixed(1)}%</div>
        <div className="text-xs text-muted-foreground">Qual rate</div>
      </div>
    </div>
  );
};

// Call Details Modal
const CallDetailsModal = ({ call, onClose }) => {
  const [tags, setTags] = useState([]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Call Details
            <Badge>{call.direction}</Badge>
          </DialogTitle>
          <DialogDescription>
            {call.phone_number} • {new Date(call.created_at).toLocaleString()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Call Metrics */}
          <div className="grid grid-cols-4 gap-4">
            <div className="border rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Duration</div>
              <div className="text-lg font-bold">
                {call.duration_seconds ? `${Math.floor(call.duration_seconds / 60)}:${(call.duration_seconds % 60).toString().padStart(2, '0')}` : 'N/A'}
              </div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Sentiment</div>
              <div className="text-lg font-bold">
                {call.sentiment_score ? `${call.sentiment_score.toFixed(1)}/5` : 'N/A'}
              </div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Deal Score</div>
              <div className="text-lg font-bold">
                {call.deal_score || 'N/A'}
              </div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Outcome</div>
              <div className="text-sm font-medium">
                {call.outcome || 'N/A'}
              </div>
            </div>
          </div>

          {/* Tags */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">Call Tags</div>
              <Button size="sm" variant="outline">
                <Tag className="h-3 w-3 mr-1" />
                Add Tag
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {tags.length === 0 ? (
                <span className="text-sm text-muted-foreground">No tags</span>
              ) : (
                tags.map((tag, idx) => <Badge key={`tag-${tag}-${idx}`} variant="secondary">{tag}</Badge>)
              )}
            </div>
          </div>

          {/* Transcript */}
          {call.transcript && (
            <div>
              <div className="text-sm font-medium mb-2">Transcript</div>
              <div className="bg-secondary p-4 rounded-lg max-h-64 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm">{call.transcript}</pre>
              </div>
            </div>
          )}

          {/* AI Summary */}
          {call.summary && (
            <div>
              <div className="text-sm font-medium mb-2">AI Summary</div>
              <div className="bg-secondary p-4 rounded-lg text-sm">
                {call.summary}
              </div>
            </div>
          )}

          {/* Qualification Data */}
          {call.qualification_data && (
            <div>
              <div className="text-sm font-medium mb-2">Qualification Data</div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(call.qualification_data).map(([key, value]) => (
                  <div key={key} className="border rounded p-3">
                    <div className="text-xs text-muted-foreground">{key.replace('_', ' ')}</div>
                    <div className="font-medium">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <Button variant="outline">
              <Play className="h-4 w-4 mr-2" />
              Play Recording
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            <Button variant="outline">
              <Forward className="h-4 w-4 mr-2" />
              Forward to Team
            </Button>
            <Button variant="outline">
              <UserPlus className="h-4 w-4 mr-2" />
              Create Deal
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VoiceAI;